import { BaseService } from './base'
import { APIPath } from '../constant'
import { CommonUtility } from '../common'

class SupportTicket {
    /**
     * Create a new support ticket
     * @param {Object} data - Ticket data
     * @param {number} data.TicketId - Should be 0 for new tickets
     * @param {string} data.RequesterName - Name of the requester
     * @param {string} data.RequesterEmail - Email of the requester
     * @param {string} [data.PhoneNumber] - Optional phone number
     * @param {number} data.IssueTypeId - Issue type ID
     * @param {string} data.Message - Ticket message
     * @param {number} [data.StatusId] - Status ID (defaults to 1 = Open)
     * @param {number} data.CreatedByUserId - User ID who created the ticket
     * @param {Array<{FilePath: string}>} [data.Attachments] - Array of attachment URLs
     * @returns {Promise} Created ticket data
     */
    async create(data) {
        const payload = {
            TicketId: data.TicketId || 0,
            RequesterName: data.RequesterName,
            RequesterEmail: data.RequesterEmail,
            PhoneNumber: data.PhoneNumber || null,
            IssueTypeId: data.IssueTypeId,
            Message: data.Message,
            StatusId: data.StatusId || 1, // Default to Open (1)
            CreatedByUserId: data.CreatedByUserId,
            Attachments: data.Attachments || [],
        }
        return BaseService.post(APIPath.supportTicketCreate, payload)
    }

    /**
     * Get support ticket by ID
     * @param {number} ticketId - Ticket ID
     * @returns {Promise} Ticket data
     */
    async getById(ticketId) {
        const params = CommonUtility.objectToParams({ ticketId })
        return BaseService.get(`${APIPath.supportTicketGetById}?${params}`)
    }

    /**
     * Get support tickets by email
     * @param {string} email - Requester email
     * @returns {Promise} Array of tickets
     */
    async getByEmail(email) {
        const params = CommonUtility.objectToParams({ requesterEmail: email })
        const url = `${APIPath.supportTicketGetByEmail}?${params}`
        // Debug logging
        if (process.env.NODE_ENV !== 'production') {
            console.log('[SupportTicket] getByEmail URL:', url)
            console.log('[SupportTicket] Email:', email)
            console.log('[SupportTicket] Encoded params:', params)
        }
        return BaseService.get(url)
    }

    /**
     * Get support tickets by status
     * @param {number} statusId - Status ID (1=Open, 2=InProgress, 3=Resolved, 4=Closed)
     * @returns {Promise} Array of tickets
     */
    async getByStatus(statusId) {
        const params = CommonUtility.objectToParams({ statusId })
        return BaseService.get(`${APIPath.supportTicketGetByStatus}?${params}`)
    }

    /**
     * Get all available issue types
     * @returns {Promise} Array of issue types
     */
    async getIssueTypes() {
        return BaseService.get(APIPath.supportTicketIssueTypes)
    }

    /**
     * Update ticket status
     * @param {number} ticketId - Ticket ID
     * @param {number} statusId - New status ID (1=Open, 2=InProgress, 3=Resolved, 4=Closed)
     * @returns {Promise} Updated ticket data
     */
    async updateStatus(ticketId, statusId) {
        const params = CommonUtility.objectToParams({ ticketId, statusId })
        return BaseService.put(`${APIPath.supportTicketUpdateStatus}?${params}`, {})
    }

    /**
     * Soft delete a ticket
     * @param {number} ticketId - Ticket ID
     * @returns {Promise} Deletion result
     */
    async delete(ticketId) {
        const params = CommonUtility.objectToParams({ ticketId })
        return BaseService.remove(`${APIPath.supportTicketDelete}?${params}`)
    }

    /**
     * Get ticket attachments
     * @param {number} ticketId - Ticket ID
     * @returns {Promise} Array of attachments
     */
    async getAttachments(ticketId) {
        const params = CommonUtility.objectToParams({ ticketId })
        return BaseService.get(`${APIPath.supportTicketAttachments}?${params}`)
    }
}

const SupportTicketService = new SupportTicket()
Object.freeze(SupportTicketService)
export { SupportTicketService }
